
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pageMetadata } from '@/data/pageMetadata';
import { updateDocumentTitle, updateMetaDescription, updateCanonicalUrl, updateRobotsTag } from '@/utils/seoUtils';
import { injectValidatedSchema, createCompliantBreadcrumbSchema } from '@/utils/seoEnhancement';
import { 
  addRockyMountSchema, 
  addGreenvilleSchema, 
  addTarboroSchema, 
  addFAQSchema, 
  addOrganizationSchema 
} from './SchemaManager';

const SEOManager = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Get current page path
    const path = location.pathname;
    
    // Set default metadata with enhanced SEO
    let title = "Carolina Trophy Roofs | #1 Roofing Contractor in Eastern North Carolina";
    let description = "Carolina Trophy Roofs is Eastern NC's premier roofing contractor serving Rocky Mount, Greenville, Wilson, and surrounding areas. Expert residential and commercial roofing services with local offices and 24/7 emergency response.";
    
    // Find matching metadata for current path
    const metadata = pageMetadata[path];
    if (metadata) {
      title = metadata.title;
      description = metadata.description;
    }
    
    // Update document title and meta description
    updateDocumentTitle(title);
    updateMetaDescription(description);
    
    // CRITICAL: Always set canonical URL for every page to prevent duplicate content issues
    // This tells Google which URL is the preferred version
    updateCanonicalUrl(path);
    
    // Only add schema for public-facing pages that don't have their own comprehensive schemas
    if (!path.includes('Blog-Page-Builder') && !path.includes('admin')) {
      // Skip auto-schema for pages with comprehensive manual schemas
      const pagesWithManualSchemas = [
        '/', '/home',                    // Home page has detailed RoofingContractor schema
        '/blog',                         // Blog pages have Article/Blog schemas  
        '/services',                     // Services page has Service schema
        '/frequently-asked-questions',   // FAQ page has comprehensive FAQPage schema
        '/locations',                    // Location pages have LocalBusiness schemas
      ];
      
      // Also skip for individual blog posts and location pages
      const hasManualSchema = pagesWithManualSchemas.some(route => path === route) ||
                              path.startsWith('/blog/') ||
                              path.startsWith('/locations/');
      
      if (!hasManualSchema) {
        // For remaining pages without manual schemas, use location-based schema
        if (path.includes('greenville')) {
          addGreenvilleSchema();
        } else if (path.includes('tarboro')) {
          addTarboroSchema();
        } else {
          // Default to Rocky Mount schema for other miscellaneous pages
          addRockyMountSchema();
        }
      }
    }
    
    // Generate and inject compliant breadcrumb schema for all pages
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      const breadcrumbs = [
        { name: 'Home', url: 'https://www.carolinatrophyroofs.com' }
      ];
      
      let currentPath = '';
      pathSegments.forEach(segment => {
        currentPath += `/${segment}`;
        let name = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Special cases for common routes
        if (segment === 'frequently-asked-questions') name = 'FAQ';
        else if (segment === 'book-inspection') name = 'Book Inspection';
        else if (segment === 'service-areas') name = 'Service Areas';
        
        breadcrumbs.push({
          name,
          url: `https://www.carolinatrophyroofs.com${currentPath}`
        });
      });
      
      const breadcrumbSchema = createCompliantBreadcrumbSchema(breadcrumbs);
      injectValidatedSchema(breadcrumbSchema);
    }
    
    // Update robots meta tag - ONLY block actual admin pages and 404
    updateRobotsTag(path);
    
  }, [location]);
  
  return null; // This component doesn't render anything
};

export default SEOManager;
